import React, { useEffect, useMemo } from 'react';
import {
  Link,
  ErrorHandler,
  LoadingState,
  Tabs,
  AddButton,
  Banner,
} from '@common/components';
import { gql, useQuery, useNavigate, useParams } from '@common/hooks';
import { Row, Col } from 'antd';
import { setBreadcrumbs } from 'works/legacy/lib/client-store';
import { StateCountsFragment } from '@jobs/graphql';
import { StaffingRequestBar } from 'works/legacy/components/staffing-request-bar';
import { CollectionFetcher } from './collection-fetcher';
import { buildDialogUrl } from '@common/services';

const STATE_COUNTS_MENU_TABS = {
  not_submitted: 'Not Submitted',
  submitted: 'Submitted',
  offers: 'Offers',
  accepted: 'Accepted',
  withdrawn: 'Withdrawn',
  rejected: 'Rejected',
};

const SupplierJobPageQuery = gql`
  query SupplierJobPageQuery($staffingRequestId: ID!) {
    staffingRequest(id: $staffingRequestId) {
      id
      name
      startDate
      endDate
      submissionsPaused
      canSupplierSubmitApplications {
        value
      }
      ...StateCountsFragment
      ...StaffingRequestBarFragment
    }
  }
  ${StaffingRequestBar.fragment}
  ${StateCountsFragment}
`;

export function SupplierStaffingRequestPage() {
  const { id: staffingRequestId, tab } = useParams();
  const navigate = useNavigate();
  const { loading, error, data } = useQuery(SupplierJobPageQuery, {
    variables: {
      staffingRequestId,
    },
  });

  const staffingRequest = data?.staffingRequest;
  const staffingRequestName = staffingRequest?.name;
  const submissionsPaused = staffingRequest?.submissionsPaused;
  const canSubmit =
    staffingRequest?.canSupplierSubmitApplications?.value || false;

  // Every time we set the breadcrumbs it will update the graphql cache
  // that triggers an update that re-render the page.
  // During the automated tests it enters in a loop.
  // TODO: Move the breadcrumbs to outside the graphql store.
  useEffect(() => {
    setBreadcrumbs([
      { name: 'Staffing Requests', path: '/works/supplier' },
      {
        name: staffingRequestName,
        path: `/works/supplier/staffing-request/${staffingRequestId}`,
      },
    ]);
  }, [staffingRequestName, staffingRequestId]);

  const effectiveTabs = useMemo(() => {
    const tabs = Object.keys(STATE_COUNTS_MENU_TABS);

    if (staffingRequest?.submissionsPaused) {
      return tabs.filter((state) => state !== 'not_submitted');
    }
    return tabs;
  }, [staffingRequest]);

  const handleChange = (tab) => {
    navigate(`/works/supplier/staffing-request/${staffingRequestId}/${tab}`);
  };

  if (!data && loading) {
    return <LoadingState />;
  }

  if (error || !data) {
    return <ErrorHandler error={error} log="StaffingRequestPage" />;
  }

  const counts = {
    not_submitted: staffingRequest.notSubmittedCandidatesCount,
    submitted: staffingRequest.waitingForReviewStaffingRequestApplicationsCount,
    offers: staffingRequest.offeredOffersStaffingRequestApplicationsCount,
    accepted: staffingRequest.acceptedStaffingRequestApplicationsCount,
    withdrawn: staffingRequest.withdrawnStaffingRequestApplicationsCount,
    rejected: staffingRequest.rejectedStaffingRequestApplicationsCount,
  };

  const addCandidateUrl = buildDialogUrl('create-candidate-drawer');

  return (
    <div className="staffing-request-page">
      <StaffingRequestBar staffingRequest={staffingRequest} />
      <Tabs
        className="sra-tabs"
        defaultActiveKey={tab}
        activeKey={tab}
        onChange={handleChange}
      >
        {effectiveTabs.map((key) => (
          <Tabs.Pane
            key={key}
            tab={
              <Tabs.Tab
                title={STATE_COUNTS_MENU_TABS[key]}
                count={counts[key] || 0}
              />
            }
          >
            <Row gutter={[42, 16]}>
              <Col xs={24}>
                <div className="staffing-request-page__add-candidate-wrapper">
                  <Link
                    className="staffing-request-page__add-candidate"
                    to={addCandidateUrl}
                  >
                    <AddButton
                      size="lg"
                      disabled={!canSubmit || submissionsPaused}
                    >
                      Candidate
                    </AddButton>
                  </Link>
                </div>
                {submissionsPaused && tab === 'not_submitted' && (
                  <Banner type="warning">
                    This staffing request is not currently accepting
                    submissions.
                  </Banner>
                )}

                {!canSubmit && tab === 'not_submitted' && (
                  <Banner type="warning">
                    You can&apos;t submit to this Staffing Request because of a
                    status change (your company no longer has permissions to
                    this unit or has been paused by{' '}
                    {staffingRequest.organization.name})
                  </Banner>
                )}
                <CollectionFetcher
                  tab={tab}
                  staffingRequest={staffingRequest}
                />
              </Col>
            </Row>
          </Tabs.Pane>
        ))}
      </Tabs>

      <style jsx>{`
        .staffing-request-page :global(.sra-tabs) {
          padding: 0 24px 24px;
        }
        :global(.staffing-request-page-content__message-warning p) {
          font-size: 16px;
          margin-bottom: 0;
          font-weight: 400;
          line-height: 22px;
        }
        .staffing-request-page__add-candidate-wrapper {
          position: relative;
          margin-bottom: 16px;
        }
        :global(.staffing-request-page .staffing-request-page__add-candidate) {
          position: absolute;
          right: 0;
          top: -60px;
        }
      `}</style>
    </div>
  );
}
